/**
 * Dải "Theo cảnh" trên thẻ nhân vật (đợt 260922 E2-P2) — logic THUẦN, tách khỏi antd để jest chạy được.
 *
 * User chốt 23/09: *"thế sau 1 đống scene thì hiển thị 1 đống luôn à"* ⇒ cảnh LIỀN NHAU cùng một bộ gộp thành một đoạn.
 * Đo dev: 75% (plan × nhân vật) mặc đúng một bộ cả tập ⇒ phần lớn thẻ chỉ còn MỘT ô.
 */

export interface SceneRun {
  /** Bộ đồ của đoạn; `null` = AI đề xuất bộ chưa có trong tủ (chưa gán). */
  lookId: string | null;
  /** Dòng trỏ vào bộ ĐÃ BỊ XOÁ (id không còn trong danh sách bộ của nhân vật). */
  broken: boolean;
  unassigned: boolean;
  /** Số thứ tự cảnh (orderIndex) của đoạn, tăng dần. */
  orders: number[];
  /** id dòng `film_scene_looks` — đổi bộ cho cả đoạn là gọi lần lượt trên các id này. */
  rowIds: string[];
  /** Có dòng người đã chọn (APPROVED) — AI không ghi đè. */
  locked: boolean;
}

interface RowLike { id: string; sceneId: string; lookId?: string | null; status: string }

/**
 * Gộp dòng gán của MỘT nhân vật trong MỘT plan thành các đoạn, theo thứ tự cảnh của plan.
 * Cảnh nhân vật vắng mặt không có dòng ⇒ không làm đứt đoạn. Dòng hỏng không gộp chung với dòng lành.
 */
export function sceneRuns(rows: RowLike[], scenes: Array<{ id: string; orderIndex: number }>, looks: Array<{ id: string }>): SceneRun[] {
  const orderOf = new Map(scenes.map((s) => [s.id, s.orderIndex]));
  const lookIds = new Set(looks.map((l) => l.id));
  const sorted = rows
    .filter((r) => orderOf.has(r.sceneId))
    .slice()
    .sort((a, b) => (orderOf.get(a.sceneId)! - orderOf.get(b.sceneId)!));
  const runs: SceneRun[] = [];
  let lastKey: string | null = null;
  for (const r of sorted) {
    const lookId = r.lookId ?? null;
    const broken = !!lookId && !lookIds.has(lookId);
    const key = lookId === null ? '∅' : `${broken ? '✗' : ''}${lookId}`;
    const last = runs[runs.length - 1];
    const order = orderOf.get(r.sceneId)!;
    if (last && lastKey === key) {
      last.orders.push(order);
      last.rowIds.push(r.id);
      last.locked = last.locked || r.status === 'APPROVED';
    } else {
      runs.push({ lookId, broken, unassigned: lookId === null, orders: [order], rowIds: [r.id], locked: r.status === 'APPROVED' });
    }
    lastKey = key;
  }
  return runs;
}

/** `[1,2,3,5]` ⇒ `"S1–S3, S5"`; đủ mọi cảnh của plan ⇒ `"Cả tập"`. */
export function formatOrders(orders: number[], allOrders: number[]): string {
  const xs = Array.from(new Set(orders)).sort((a, b) => a - b);
  const all = Array.from(new Set(allOrders));
  if (xs.length && xs.length === all.length && all.every((o) => xs.includes(o))) return 'Cả tập';
  const out: string[] = [];
  for (let i = 0; i < xs.length; i++) {
    let j = i;
    while (j + 1 < xs.length && xs[j + 1] === xs[j] + 1) j++;
    out.push(i === j ? `S${xs[i]}` : `S${xs[i]}–S${xs[j]}`);
    i = j;
  }
  return out.join(', ');
}

export type LookUsageMap = Record<string, { plans: Array<{ planId: string; planName: string; sceneOrders: number[] }>; promptEvents: number }>;

/**
 * Bộ đồ là dữ liệu CẤP TRUYỆN — sửa/xoá ở plan này là đổi cho mọi plan đang dùng nó. User chốt không làm tầng "theo
 * tập" (*"đang làm ở plan nào thì vào luôn trong plan đó sửa"*) ⇒ chỉ nhắc một câu khi bộ còn được plan KHÁC dùng.
 */
export function lookUsageNote(usage: LookUsageMap | null | undefined, lookId: string, currentPlanId?: string | null): string | null {
  const u = usage?.[lookId];
  if (!u) return null;
  const others = u.plans.filter((p) => p.planId !== currentPlanId);
  if (!others.length) return null;
  return `còn được dùng ở ${others.map((p) => p.planName).join(', ')} — sửa hoặc xoá ở đây là đổi luôn cho các plan đó.`;
}
